import { TableColumn } from '@/modules/app/components/ui/HorizontalTable';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface ExportOptions {
  filename?: string;
  includeTimestamp?: boolean;
  customHeaders?: Record<string, string>;
}

export interface PDFExportOptions extends ExportOptions {
  title?: string;
  subtitle?: string;
  orientation?: 'portrait' | 'landscape';
  workspaceName?: string;
  tabName?: string;
}

/**
 * Export data to CSV format
 */
export const exportToCSV = (
  data: Record<string, any>[] | any[],
  columns: TableColumn[],
  options: ExportOptions = {}
): void => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  const filename = options.filename || 'export';
  const timestamp = options.includeTimestamp !== false ? new Date().toISOString().split('T')[0] : '';
  const finalFilename = `${filename}${timestamp ? `_${timestamp}` : ''}.csv`;

  // Create CSV headers
  const headers = columns.map(col => options.customHeaders?.[col.key] || col.label);
  
  // Create CSV rows
  const csvRows = [
    headers.join(','),
    ...data.map(row => 
      columns.map(col => {
        const value = row[col.key];
        const stringValue = value == null ? '' : String(value);
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
          ? `"${stringValue.replace(/"/g, '""')}"`
          : stringValue;
      }).join(',')
    )
  ];

  // Create and download file
  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, finalFilename);
};

/**
 * Export data to JSON format
 */
export const exportToJSON = (
  data: Record<string, unknown>[],
  columns: TableColumn[],
  options: ExportOptions = {}
): void => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  const filename = options.filename || 'export';
  const timestamp = options.includeTimestamp !== false ? new Date().toISOString().split('T')[0] : '';
  const finalFilename = `${filename}${timestamp ? `_${timestamp}` : ''}.json`;

  // Filter data to only include columns that are being displayed
  const filteredData = data.map(row => {
    const filteredRow: Record<string, unknown> = {};
    columns.forEach(col => {
      const displayKey = options.customHeaders?.[col.key] || col.label;
      filteredRow[displayKey] = row[col.key];
    });
    return filteredRow;
  });

  const jsonContent = JSON.stringify(filteredData, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  downloadBlob(blob, finalFilename);
};

/**
 * Export data to PDF format using jsPDF with autoTable
 */
export const exportToPDF = async (
  data: Record<string, any>[] | any[],
  columns: TableColumn[],
  options: PDFExportOptions = {}
): Promise<void> => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  try {
    const filename = options.filename || 'export';
    const timestamp = options.includeTimestamp !== false ? new Date().toISOString().split('T')[0] : '';
    const finalFilename = `${filename}${timestamp ? `_${timestamp}` : ''}.pdf`;
    
    // Determine orientation based on number of columns
    const orientation = options.orientation || (columns.length > 6 ? 'landscape' : 'portrait');
    
    // Create PDF document
    const doc = new jsPDF({
      orientation,
      unit: 'pt',
      format: 'a4'
    });

    // Add title and metadata
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let yPosition = margin;

    // Title
    if (options.title) {
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(options.title, margin, yPosition);
      yPosition += 25;
    }

    // Subtitle
    if (options.subtitle) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(options.subtitle, margin, yPosition);
      yPosition += 15;
    }

    // Workspace and tab info
    if (options.workspaceName || options.tabName) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      const contextInfo = [];
      if (options.workspaceName) contextInfo.push(`Workspace: ${options.workspaceName}`);
      if (options.tabName) contextInfo.push(`Tab: ${options.tabName}`);
      doc.text(contextInfo.join(' • '), margin, yPosition);
      yPosition += 15;
    }

    // Export date and record count
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const exportDate = new Date().toLocaleDateString();
    const recordCount = data.length.toLocaleString();
    doc.text(`Exported: ${exportDate} • Records: ${recordCount}`, margin, yPosition);
    yPosition += 20;

    // Prepare table data
    const tableHeaders = columns.map(col => options.customHeaders?.[col.key] || col.label);
    const tableData = data.map(row => 
      columns.map(col => {
        const value = row[col.key];
        if (value === null || value === undefined) return '';
        
        // Format specific data types for PDF
        if (col.key.includes('date') && value) {
          return new Date(value).toLocaleDateString();
        }
        if (col.key.includes('volume') && typeof value === 'number') {
          return value.toLocaleString();
        }
        if (col.key.includes('cpc') && typeof value === 'number') {
          return `$${value.toFixed(2)}`;
        }
        if (col.key.includes('score') && typeof value === 'number') {
          return Math.round(value).toString();
        }
        
        // Handle long text fields - truncate if too long
        const stringValue = String(value);
        if (col.key.includes('description') || col.key.includes('reasoning') || col.key.includes('focus')) {
          return stringValue.length > 200 ? stringValue.substring(0, 200) + '...' : stringValue;
        }
        if (col.key.includes('keywords') || col.key.includes('terms') || col.key.includes('variations')) {
          return stringValue.length > 150 ? stringValue.substring(0, 150) + '...' : stringValue;
        }
        
        return stringValue;
      })
    );

    // Create table using autoTable
    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY: yPosition,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'left',
        valign: 'top',
        lineColor: [200, 200, 200],
        lineWidth: 0.3
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      columnStyles: {
        // Enhanced column styling based on column type
        ...columns.reduce((acc, col, index) => {
          // Wide text columns
          if (col.key.includes('description') || col.key.includes('reasoning') || col.key.includes('focus') || col.key.includes('recommendations')) {
            acc[index] = { 
              cellWidth: 'auto',
              minCellWidth: 100,
              maxCellWidth: 150,
              fontSize: 6
            };
          }
          // URL columns
          else if (col.key.includes('url')) {
            acc[index] = { 
              cellWidth: 'auto',
              minCellWidth: 80,
              maxCellWidth: 120,
              fontSize: 6
            };
          }
          // Keyword columns
          else if (col.key.includes('keywords') || col.key.includes('terms') || col.key.includes('variations')) {
            acc[index] = { 
              cellWidth: 'auto',
              minCellWidth: 70,
              maxCellWidth: 100,
              fontSize: 6
            };
          }
          // Short text columns
          else if (col.key.includes('category') || col.key.includes('status') || col.key.includes('type')) {
            acc[index] = { 
              cellWidth: 'auto',
              minCellWidth: 40,
              maxCellWidth: 60,
              halign: 'center'
            };
          }
          // Number columns
          else if (col.key.includes('score') || col.key.includes('volume') || col.key.includes('cpc') || col.key.includes('difficulty')) {
            acc[index] = { 
              cellWidth: 'auto',
              minCellWidth: 30,
              maxCellWidth: 50,
              halign: 'right'
            };
          }
          // Date columns
          else if (col.key.includes('date') || col.key.includes('created') || col.key.includes('updated')) {
            acc[index] = { 
              cellWidth: 'auto',
              minCellWidth: 40,
              maxCellWidth: 60,
              halign: 'center',
              fontSize: 6
            };
          }
          return acc;
        }, {} as Record<number, any>)
      },
      didDrawPage: (hookData) => {
        // Add footer with page numbers
        const pageNumber = doc.internal.getNumberOfPages();
        const currentPage = hookData.pageNumber;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Page ${currentPage} of ${pageNumber}`,
          pageWidth - margin - 50,
          doc.internal.pageSize.getHeight() - 20
        );
      }
    });

    // Save the PDF
    doc.save(finalFilename);
    
  } catch (error) {
    console.error('PDF export failed:', error);
    throw new Error('PDF export failed. Please try again.');
  }
};

/**
 * Export data to Excel format (requires xlsx library)
 */
export const exportToExcel = async (
  data: Record<string, any>[] | any[],
  columns: TableColumn[],
  options: ExportOptions = {}
): Promise<void> => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  try {
    // Dynamic import of xlsx library
    const XLSX = await import('xlsx');
    
    const filename = options.filename || 'export';
    const timestamp = options.includeTimestamp !== false ? new Date().toISOString().split('T')[0] : '';
    const finalFilename = `${filename}${timestamp ? `_${timestamp}` : ''}.xlsx`;

    // Prepare data for Excel
    const excelData = data.map(row => {
      const excelRow: Record<string, unknown> = {};
      columns.forEach(col => {
        const displayKey = options.customHeaders?.[col.key] || col.label;
        excelRow[displayKey] = row[col.key];
      });
      return excelRow;
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Auto-size columns
    const columnWidths = columns.map(col => ({
      wch: Math.max(
        (options.customHeaders?.[col.key] || col.label).length,
        Math.max(...data.map(row => String(row[col.key] || '').length))
      )
    }));
    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
    
    // Write file
    XLSX.writeFile(workbook, finalFilename);
    
  } catch (error) {
    console.error('Excel export failed:', error);
    throw new Error('Excel export failed. Make sure xlsx library is installed.');
  }
};

/**
 * Helper function to download a blob as a file
 */
const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Get export filename with context
 */
export const getExportFilename = (
  baseContext: string,
  tabName?: string,
  workspaceName?: string
): string => {
  const parts = [baseContext];
  
  if (workspaceName) {
    parts.push(workspaceName.toLowerCase().replace(/[^a-z0-9]/g, '_'));
  }
  
  if (tabName) {
    parts.push(tabName.toLowerCase().replace(/[^a-z0-9]/g, '_'));
  }
  
  return parts.join('_');
};

/**
 * Format export data for better readability
 */
export const formatExportData = (data: Record<string, any>[] | any[]): Record<string, unknown>[] => {
  return data.map(row => {
    const formattedRow = { ...row };
    
    // Format dates
    Object.keys(formattedRow).forEach(key => {
      if (key.includes('date') || key.includes('created_at') || key.includes('updated_at')) {
        if (formattedRow[key]) {
          formattedRow[key] = new Date(formattedRow[key]).toLocaleDateString();
        }
      }
      
      // Format numbers
      if (key.includes('volume') || key.includes('search_volume') || key.includes('msv')) {
        if (typeof formattedRow[key] === 'number') {
          formattedRow[key] = formattedRow[key].toLocaleString();
        }
      }
      
      // Format CPC
      if (key.includes('cpc') && typeof formattedRow[key] === 'number') {
        formattedRow[key] = `$${formattedRow[key].toFixed(2)}`;
      }
    });
    
    return formattedRow;
  });
};